import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input, Label, Textarea } from './Input';
import { ImageUpload } from './ImageUpload';
import { useToast } from './Toast';
// MIGRATION FIREBASE: Utilise Firebase direct au lieu de l'API Python
import { choiceLibraryService } from '../services/firebaseService';

export const ChoiceLibraryModal = ({ isOpen, onClose, choice, onSuccess }) => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    name: '',
    default_price: 0,
    image_url: '',
    description: ''
  });
  
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (choice) {
      setFormData({
        name: choice.name || '',
        default_price: choice.default_price || 0,
        image_url: choice.image_url || '',
        description: choice.description || ''
      });
    } else {
      setFormData({
        name: '',
        default_price: 0,
        image_url: '',
        description: ''
      });
    }
  }, [choice, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // FIREBASE DIRECT
      if (choice) {
        // Update existing choice
        const result = await choiceLibraryService.update(choice.id, formData);
        const syncedCount = result?.synced_options || 0;
        if (syncedCount > 0) {
          toast.success('Synchronisé !', `Choix modifié et ${syncedCount} option(s) mise(s) à jour automatiquement`);
        } else {
          toast.success('Succès', 'Choix modifié avec succès');
        }
      } else {
        // Create new choice
        await choiceLibraryService.create(formData);
        toast.success('Succès', 'Choix créé avec succès');
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error saving choice:', error);
      toast.error('Erreur', 'Erreur lors de l\'enregistrement du choix');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title={choice ? '✏️ Modifier le choix' : '➕ Nouveau choix'}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-600 mb-4">
          Créez un choix réutilisable que vous pourrez utiliser dans plusieurs options
        </p>

        {/* Nom */}
        <div>
          <Label htmlFor="name">Nom du choix *</Label>
          <Input
            id="name"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            placeholder="Ex: Chantilly, Ketchup, Bacon..."
            required
          />
        </div>

        {/* Prix par défaut */}
        <div>
          <Label htmlFor="default_price">Prix par défaut</Label>
          <Input
            id="default_price"
            type="number"
            step="0.01"
            min="0"
            value={formData.default_price}
            onChange={(e) => setFormData({...formData, default_price: parseFloat(e.target.value) || 0})}
            placeholder="0.00"
          />
          <p className="text-xs text-gray-500 mt-1">
            Ce prix sera utilisé par défaut, mais vous pourrez le modifier pour chaque option
          </p>
        </div>

        {/* Image Upload avec recadrage */}
        <ImageUpload
          currentImage={formData.image_url}
          onImageChange={(url) => setFormData({...formData, image_url: url})}
          label="Image (optionnel)"
          folder="choices"
          aspectRatio={1}
        />

        {/* Description */}
        <div>
          <Label htmlFor="description">Description (optionnel)</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            placeholder="Description du choix..."
            rows={2}
          />
        </div>

        {/* Buttons */}
        <div className="flex gap-2 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="flex-1"
            disabled={loading}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            className="flex-1"
            disabled={loading}
          >
            {loading ? 'Enregistrement...' : (choice ? 'Modifier' : 'Créer')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
